import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = 'https://dl6poek2ig.execute-api.us-east-1.amazonaws.com/prod';

class ApiService {
  async getAuthHeaders() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw error;
    }
  }

  async getUserGroups() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken;
      
      if (!token) {
        return [];
      }
      
      // Parse the JWT token to get user groups
      const payload = JSON.parse(atob(token.toString().split('.')[1]));
      return payload['cognito:groups'] || [];
    } catch (error) {
      console.error('Error getting user groups:', error);
      return [];
    }
  }

  async isSuperUser() {
    try {
      const groups = await this.getUserGroups();
      return groups.includes('SuperUser');
    } catch (error) {
      console.error('Error checking super user status:', error);
      return false;
    }
  }

  async getAllApplications() {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const applications = data.applications || [];
      
      // Ensure backward compatibility with older data structures
      return applications.map(app => ({
        ...app,
        // Map questions to formAnswers for backward compatibility
        formAnswers: app.formAnswers || app.questions || [],
        // Ensure score is available from both old and new structure
        score: app.score || app.scoring?.currentTotal || 0
      }));
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  }

  async getApplicationById(id) {
    // For now, we'll get all applications and filter
    // In the future, we could create a specific endpoint for single application
    try {
      const applications = await this.getAllApplications();
      const application = applications.find(app => app.id === id || app.email === id);
      
      // Ensure backward compatibility with older data structures
      if (application && !application.formAnswers && application.questions) {
        application.formAnswers = application.questions;
      }
      
      return application;
    } catch (error) {
      console.error('Error fetching application:', error);
      throw error;
    }
  }

  async updateAssessmentScores(applicationId, scores) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/scores`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ scores })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to edit this assessment.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating assessment scores:', error);
      throw error;
    }
  }

  async getAvailableInterviewers(site = null) {
    try {
      const headers = await this.getAuthHeaders();
      
      // Build URL with optional site parameter
      let url = `${API_BASE_URL}/admin/interviewers`;
      if (site) {
        url += `?site=${encodeURIComponent(site)}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const interviewers = data.interviewers || [];
      
      // Client-side filtering as backup if backend doesn't filter
      if (site && interviewers.length > 0) {
        return interviewers.filter(interviewer => {
          const interviewerSite = interviewer.site || interviewer.location;
          return interviewerSite && interviewerSite.toLowerCase() === site.toLowerCase();
        });
      }
      
      return interviewers;
    } catch (error) {
      console.error('Error fetching interviewers:', error);
      throw error;
    }
  }

  async addInterviewer(interviewerData) {
    try {
      const headers = await this.getAuthHeaders();
      
      // Transform the data to match the expected API format
      const apiData = {
        email: interviewerData.email,
        name: interviewerData.name,
        calendly: interviewerData.calendlyLink || '',
        site: interviewerData.site
      };
      
      const response = await fetch(`${API_BASE_URL}/superuser/add-admin`, {
        method: 'POST',
        headers,
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to add interviewers.');
        }
        if (response.status === 409) {
          throw new Error('An interviewer with this email already exists.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error adding interviewer:', error);
      throw error;
    }
  }

  async assignInterviewer(applicationId, interviewerEmail) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/assign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ interviewerEmail })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to assign interviewers.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error assigning interviewer:', error);
      throw error;
    }
  }

  async removeInterviewer(applicationId, interviewerEmail) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/remove`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ interviewerEmail })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to remove interviewers.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error removing interviewer:', error);
      throw error;
    }
  }

  async finalizeApplication(applicationId, finalizeData) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/finalize`, {
        method: 'POST',
        headers,
        body: JSON.stringify(finalizeData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to finalize this application.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error finalizing application:', error);
      throw error;
    }
  }

  async getQuestionnaire(email) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/questionnaire?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 404) {
          throw new Error('Questionnaire not found for this email.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      throw error;
    }
  }

  async updateQuestionnaire(email, answers) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/questionnaire`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ email, answers })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating questionnaire:', error);
      throw error;
    }
  }

  generateSignedDownloadUrl(s3Key) {
    // Generate a signed URL for downloading the PowerPoint file
    // This would need to be implemented as another Lambda endpoint
    // For now, return a placeholder URL
    return `${API_BASE_URL}/download/${encodeURIComponent(s3Key)}`;
  }

  async getQuestionnaireResponses(applicationId) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/questionnaire`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting questionnaire responses:', error);
      throw error;
    }
  }

  async getInterviewQuestions(applicationId) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/interview-questions`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting interview questions:', error);
      throw error;
    }
  }

  async saveInterviewQuestions(applicationId, questionsData) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/interview-questions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(questionsData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving interview questions:', error);
      throw error;
    }
  }

  async updateDecision(applicationId, decision) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/admin/applications/${applicationId}/decision`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ decision })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating decision:', error);
      throw error;
    }
  }

  async updateInterviewer(email, interviewerData) {
    try {
      const headers = await this.getAuthHeaders();
      
      // Transform the data to match the expected API format
      const apiData = {
        name: interviewerData.name,
        calendly: interviewerData.calendlyLink || interviewerData.calendly || '',
        site: interviewerData.site
      };
      
      const response = await fetch(`${API_BASE_URL}/superuser/interviewers/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to update interviewers.');
        }
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Invalid data provided.');
        }
        if (response.status === 404) {
          throw new Error('Interviewer not found.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating interviewer:', error);
      throw error;
    }
  }

  async deleteInterviewer(email) {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/superuser/interviewers/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 403) {
          throw new Error('You are not authorized to delete interviewers.');
        }
        if (response.status === 404) {
          throw new Error('Interviewer not found.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting interviewer:', error);
      throw error;
    }
  }
}

const apiService = new ApiService();
export default apiService;
